import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { signOut, useTranslation } from '@dukanti/shared';
import type { TranslationKey } from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { supabase } from '@/lib/supabase';
import { useRequestAdminEmailOtp, useVerifyAdminEmailOtp } from '@/hooks/use-signin';

const emailSigninSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, { message: 'admin.signIn.errors.emailRequired' })
    .email({ message: 'admin.signIn.errors.emailInvalid' }),
});

const emailCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, { message: 'auth.phoneOtp.errors.codeRequired' })
    .regex(/^[0-9]{6}$/, { message: 'auth.phoneOtp.errors.codeInvalid' }),
});

type EmailSigninValues = z.infer<typeof emailSigninSchema>;
type EmailCodeValues = z.infer<typeof emailCodeSchema>;
type AdminAccessState = 'member' | 'pending' | 'unauthorized';

const ADMIN_OTP_REQUEST_TIMEOUT_MS = 15_000;
const ADMIN_OTP_REQUEST_TIMEOUT_CODE = 'admin_otp_request_timeout';

export function SigninForm() {
  const { t, lang } = useTranslation();
  const navigate = useNavigate();
  const [otpEmail, setOtpEmail] = useState<string | null>(null);
  const [otpError, setOtpError] = useState<TranslationKey | null>(null);
  const [accessState, setAccessState] = useState<Exclude<AdminAccessState, 'member'> | null>(null);
  const [isRequestingCode, setIsRequestingCode] = useState(false);
  const requestInFlightRef = useRef(false);
  const activeRequestAttemptRef = useRef(0);
  const isMountedRef = useRef(true);
  const requestOtp = useRequestAdminEmailOtp();
  const verifyOtp = useVerifyAdminEmailOtp();

  const emailForm = useForm<EmailSigninValues>({
    resolver: zodResolver(emailSigninSchema),
    mode: 'onBlur',
    defaultValues: { email: '' },
  });
  const codeForm = useForm<EmailCodeValues>({
    resolver: zodResolver(emailCodeSchema),
    mode: 'onBlur',
    defaultValues: { code: '' },
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!window.matchMedia('(pointer: fine)').matches) return;
    emailForm.setFocus('email');
  }, [emailForm]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const runBoundedOtpRequest = async (email: string): Promise<boolean> => {
    if (requestInFlightRef.current) return false;

    const attemptId = activeRequestAttemptRef.current + 1;
    activeRequestAttemptRef.current = attemptId;
    requestInFlightRef.current = true;
    setIsRequestingCode(true);

    try {
      await withOtpRequestDeadline(
        requestOtp.mutateAsync({ email, preferredLocale: lang }),
        ADMIN_OTP_REQUEST_TIMEOUT_MS,
      );
      return activeRequestAttemptRef.current === attemptId && isMountedRef.current;
    } catch (err) {
      if (activeRequestAttemptRef.current === attemptId && isMountedRef.current) {
        setOtpError(mapAdminEmailOtpError(err));
      }
      return false;
    } finally {
      if (activeRequestAttemptRef.current === attemptId) {
        requestInFlightRef.current = false;
        if (isMountedRef.current) {
          setIsRequestingCode(false);
        }
      }
    }
  };

  const onRequestOtp = async (values: EmailSigninValues): Promise<void> => {
    if (requestInFlightRef.current) return;
    setOtpError(null);
    setAccessState(null);
    const email = values.email.trim().toLowerCase();
    const didSendCurrentAttempt = await runBoundedOtpRequest(email);
    if (didSendCurrentAttempt) {
      setOtpEmail(email);
      codeForm.reset({ code: '' });
    }
  };

  const onVerifyOtp = async (values: EmailCodeValues): Promise<void> => {
    if (!otpEmail) return;
    setOtpError(null);
    setAccessState(null);
    try {
      const result = await verifyOtp.mutateAsync({ email: otpEmail, token: values.code });
      if (!result.user) {
        throw { code: 'admin_account_missing' };
      }

      const access = await getAdminAccessStatus(result.user.id);
      if (access === 'member') {
        navigate('/dashboard', { replace: true });
        return;
      }

      setAccessState(access);
    } catch (err) {
      setOtpError(mapAdminEmailOtpError(err));
    }
  };

  const onResendOtp = async (): Promise<void> => {
    if (!otpEmail || requestInFlightRef.current) return;
    setOtpError(null);
    const didSendCurrentAttempt = await runBoundedOtpRequest(otpEmail);
    if (didSendCurrentAttempt) {
      codeForm.reset({ code: '' });
    }
  };

  if (accessState) {
    return <AdminAccessStatePanel state={accessState} />;
  }

  return (
    <div className="space-y-4">
      {otpError ? (
        <div
          role="alert"
          className="border-destructive/30 bg-destructive/10 text-destructive rounded-lg border p-3 text-start text-sm"
          data-testid="signin-otp-error"
        >
          {t(otpError)}
        </div>
      ) : null}

      {!otpEmail ? (
        <form noValidate onSubmit={emailForm.handleSubmit(onRequestOtp)} className="space-y-4">
          <div>
            <label
              htmlFor="signin-email"
              className="text-foreground block text-start text-sm font-medium"
            >
              {t('admin.signIn.emailLabel')}
            </label>
            <Input
              id="signin-email"
              type="email"
              dir="ltr"
              inputMode="email"
              autoComplete="email"
              data-testid="signin-email"
              aria-invalid={!!emailForm.formState.errors.email}
              aria-describedby={emailForm.formState.errors.email ? 'signin-email-error' : undefined}
              className="mt-1"
              {...emailForm.register('email')}
            />
            {emailForm.formState.errors.email ? (
              <p
                id="signin-email-error"
                role="alert"
                className="text-destructive mt-1 text-start text-sm"
                data-testid="signin-email-error"
              >
                {t(emailForm.formState.errors.email.message as TranslationKey)}
              </p>
            ) : null}
          </div>
          <Button
            type="submit"
            disabled={isRequestingCode}
            aria-disabled={isRequestingCode}
            className="w-full"
            data-testid="signin-request-code"
          >
            {isRequestingCode ? t('auth.phoneOtp.requestingCode') : t('auth.phoneOtp.requestCode')}
          </Button>
        </form>
      ) : (
        <form noValidate onSubmit={codeForm.handleSubmit(onVerifyOtp)} className="space-y-4">
          <div>
            <label
              htmlFor="signin-code"
              className="text-foreground block text-start text-sm font-medium"
            >
              {t('auth.phoneOtp.codeLabel')}
            </label>
            <Input
              id="signin-code"
              type="text"
              dir="ltr"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              data-testid="signin-code"
              aria-invalid={!!codeForm.formState.errors.code}
              aria-describedby={codeForm.formState.errors.code ? 'signin-code-error' : undefined}
              {...codeForm.register('code')}
            />
            {codeForm.formState.errors.code ? (
              <p
                id="signin-code-error"
                role="alert"
                className="text-destructive mt-1 text-start text-sm"
                data-testid="signin-code-error"
              >
                {t(codeForm.formState.errors.code.message as TranslationKey)}
              </p>
            ) : null}
          </div>
          <Button
            type="submit"
            disabled={verifyOtp.isPending}
            aria-disabled={verifyOtp.isPending}
            className="w-full"
            data-testid="signin-verify-code"
          >
            {verifyOtp.isPending ? t('auth.phoneOtp.verifyingCode') : t('auth.phoneOtp.verifyCode')}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={isRequestingCode}
            aria-disabled={isRequestingCode}
            className="w-full"
            onClick={onResendOtp}
            data-testid="signin-resend-code"
          >
            {isRequestingCode ? t('auth.phoneOtp.requestingCode') : t('auth.phoneOtp.resendCode')}
          </Button>
        </form>
      )}
    </div>
  );
}

function AdminAccessStatePanel({ state }: { state: Exclude<AdminAccessState, 'member'> }) {
  const { t } = useTranslation();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async (): Promise<void> => {
    setIsSigningOut(true);
    try {
      await signOut(supabase);
      window.location.assign('/auth/signin');
    } finally {
      setIsSigningOut(false);
    }
  };

  const isPending = state === 'pending';

  return (
    <section
      data-testid={isPending ? 'admin-access-pending-state' : 'admin-access-unauthorized-state'}
      className="space-y-4 text-start"
    >
      <div className="border-border bg-card rounded-xl border p-5 shadow-sm">
        <h2 className="font-display text-foreground text-2xl">
          {isPending
            ? t('admin.authBoundary.pendingTitle')
            : t('admin.authBoundary.unauthorizedTitle')}
        </h2>
        <p className="text-muted-foreground mt-3 text-sm leading-6">
          {isPending
            ? t('admin.authBoundary.pendingBody')
            : t('admin.authBoundary.unauthorizedBody')}
        </p>
      </div>
      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={handleSignOut}
        disabled={isSigningOut}
        aria-disabled={isSigningOut}
        data-testid="admin-access-signout"
      >
        {t('admin.authBoundary.signOut')}
      </Button>
    </section>
  );
}

async function getAdminAccessStatus(userId: string): Promise<AdminAccessState> {
  const { data: userData, error: userError } = await supabase.auth.getUser();
  if (userError) throw userError;
  const authUser = userData.user;
  if (!authUser || authUser.id !== userId || !authUser.email || authUser.phone) {
    return 'unauthorized';
  }

  const { data, error } = await supabase
    .from('store_members')
    .select('store_id')
    .eq('user_id', userId)
    .is('deleted_at', null)
    .limit(1)
    .returns<Array<{ store_id: string }>>();
  if (error) throw error;

  return data && data.length > 0 ? 'member' : 'pending';
}

function withOtpRequestDeadline<T>(request: Promise<T>, timeoutMs: number): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject({ code: ADMIN_OTP_REQUEST_TIMEOUT_CODE });
    }, timeoutMs);
  });

  return Promise.race([request, timeout]).finally(() => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  });
}

function mapAdminEmailOtpError(err: unknown): TranslationKey {
  if (isAdminOtpRequestTimeout(err)) {
    return 'admin.signIn.errors.serviceUnavailable';
  }

  if (err instanceof TypeError && /fetch/i.test(err.message)) {
    return 'admin.signIn.errors.networkFailed';
  }

  if (err && typeof err === 'object') {
    const maybe = err as { status?: number; code?: string; error?: string; message?: string };
    if (maybe.status === 429) return 'auth.phoneOtp.errors.rateLimited';
    const haystack = `${maybe.code ?? ''} ${maybe.error ?? ''} ${maybe.message ?? ''}`;
    if (
      maybe.status === 504 ||
      maybe.code === 'request_timeout' ||
      /deadline|gateway\s+timeout|request\s+timed\s+out|service\s+unavailable|timeout/i.test(
        haystack,
      )
    ) {
      return 'admin.signIn.errors.serviceUnavailable';
    }
    if (/otp|token|code|invalid|expired/i.test(haystack)) {
      return 'auth.phoneOtp.errors.invalidCode';
    }
  }

  return 'admin.signIn.errors.unknown';
}

function isAdminOtpRequestTimeout(err: unknown): boolean {
  return (
    !!err &&
    typeof err === 'object' &&
    'code' in err &&
    (err as { code?: unknown }).code === ADMIN_OTP_REQUEST_TIMEOUT_CODE
  );
}
